import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {
  collectLocationQuestionOptions,
  collectUserQuestionOptions,
} from 'src/modules/tarea/formatoQuestionOptions';
import {
  DEFAULT_LABEL_BY_TARGET,
  EXECUTOR_SOURCE_OPTIONS,
  IS_TASK_TARGET_OPTIONS,
  MULTIPLE_BY_TARGET,
  VALIDATOR_SOURCE_OPTIONS,
  buildDefaultIsTaskConfig,
  inferResponseType,
} from 'src/modules/tarea/isTaskConfig';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';

type Props = {
  subsectionName: string;
};

const TARGET_ORDER: Record<string, number> = Object.fromEntries(IS_TASK_TARGET_OPTIONS.map((o, i) => [o.value, i]));

function getTargetOrder(taskTarget: string): number {
  return TARGET_ORDER[taskTarget] ?? 999;
}

const DEFAULT_OPTIONS_PRIORIDAD = [
  {id: 'Baja', label: 'Baja'},
  {id: 'Media', label: 'Media'},
  {id: 'Alta', label: 'Alta'},
];

function createFieldForTarget(taskTarget: string) {
  const label = DEFAULT_LABEL_BY_TARGET[taskTarget] ?? taskTarget;
  const responseType = inferResponseType(taskTarget, '');
  const options = taskTarget === 'prioridad' ? DEFAULT_OPTIONS_PRIORIDAD : ([] as {id: string; label: string}[]);
  return {
    key: taskTarget,
    label,
    responseType,
    required: ['modeloTarea', 'titulo', 'ejecutor'].includes(taskTarget),
    isMultiple: MULTIPLE_BY_TARGET.has(taskTarget),
    defaultValue: '',
    options,
    taskTarget,
    ...(taskTarget === 'ejecutor' ? {executorSource: 'form' as const} : {}),
    ...(taskTarget === 'validadores' ? {validatorSource: 'form' as const} : {}),
  };
}

export default function IsTaskConfigForm({subsectionName}: Props) {
  const {control, setValue, getValues} = useFormContext();
  const taskConfigPath = `${subsectionName}.taskConfig`;
  const fieldsPath = `${taskConfigPath}.fields`;

  const {fields, append, insert, remove, replace} = useFieldArray({
    control,
    name: fieldsPath,
  });

  const fieldValues = useWatch({control, name: fieldsPath, defaultValue: []}) as any[];
  const formato = useWatch({control, name: 'formato'});
  const selectedTargets = new Set((fieldValues || []).map((f) => f?.taskTarget).filter(Boolean));
  const locationQuestionOptions = useMemo(
    () => collectLocationQuestionOptions(formato, subsectionName),
    [formato, subsectionName],
  );
  const userQuestionOptions = useMemo(
    () => collectUserQuestionOptions(formato, subsectionName),
    [formato, subsectionName],
  );
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(false);

  useEffect(() => {
    const currentConfig = getValues(taskConfigPath);
    const currentFields = currentConfig?.fields ?? [];
    if (currentFields.length > 0) return;
    const defaults = buildDefaultIsTaskConfig();
    setValue(taskConfigPath, {...defaults, fields: []}, {shouldDirty: true});
    replace(defaults.fields);
  }, [getValues, setValue, taskConfigPath, replace]);

  const sortedFieldValues = useMemo(() => {
    const list = [...(fieldValues || [])];
    return list.sort((a, b) => getTargetOrder(a?.taskTarget) - getTargetOrder(b?.taskTarget));
  }, [fieldValues]);

  useEffect(() => {
    if (!fieldValues?.length) return;
    const currentOrder = (fieldValues || []).map((f) => f?.taskTarget).join(',');
    const correctOrder = sortedFieldValues.map((f) => f?.taskTarget).join(',');
    if (currentOrder !== correctOrder) {
      replace(sortedFieldValues);
    }
  }, [fieldValues, sortedFieldValues, replace]);

  useEffect(() => {
    const list = fieldValues || [];
    const seen = new Set<string>();
    const toRemove: number[] = [];
    list.forEach((field, index) => {
      const taskTarget = field?.taskTarget;
      if (!taskTarget) return;
      if (seen.has(taskTarget)) {
        toRemove.push(index);
      } else {
        seen.add(taskTarget);
      }
    });
    if (toRemove.length) {
      toRemove.sort((a, b) => b - a).forEach((idx) => remove(idx));
    }
  }, [fieldValues, remove]);

  useEffect(() => {
    (fieldValues || []).forEach((field, index) => {
      const taskTarget = field?.taskTarget;
      if (!taskTarget) return;
      const keyPath = `${fieldsPath}.${index}.key`;
      const currentKey = getValues(keyPath);
      if (currentKey !== taskTarget) {
        setValue(keyPath, taskTarget, {shouldDirty: true});
      }
      const responseTypePath = `${fieldsPath}.${index}.responseType`;
      const currentRt = getValues(responseTypePath);
      const expectedRt = inferResponseType(taskTarget, currentRt ?? '');
      if (currentRt !== expectedRt) {
        setValue(responseTypePath, expectedRt, {shouldDirty: true});
      }
    });
  }, [fieldValues, fieldsPath, getValues, setValue]);

  const handleToggleTarget = useCallback(
    (taskTarget: string, checked: boolean) => {
      const otherOfPair = taskTarget === 'modeloTarea' ? 'titulo' : taskTarget === 'titulo' ? 'modeloTarea' : null;

      if (checked) {
        if (otherOfPair && selectedTargets.has(otherOfPair)) {
          const otherIdx = fieldValues?.findIndex((f) => f?.taskTarget === otherOfPair);
          if (typeof otherIdx === 'number' && otherIdx >= 0) remove(otherIdx);
        }
        const existing = fieldValues?.find((f) => f?.taskTarget === taskTarget);
        if (existing) return;
        const newField = createFieldForTarget(taskTarget);
        const order = getTargetOrder(taskTarget);
        let insertIndex = (fieldValues || []).length;
        for (let i = 0; i < (fieldValues || []).length; i++) {
          if (getTargetOrder(fieldValues[i]?.taskTarget) > order) {
            insertIndex = i;
            break;
          }
        }
        insert(insertIndex, newField);
        setExpandedAccordion(insertIndex);
      } else {
        if (otherOfPair || taskTarget === 'ejecutor') return;
        const idx = fieldValues?.findIndex((f) => f?.taskTarget === taskTarget);
        if (typeof idx === 'number' && idx >= 0) {
          remove(idx);
          setExpandedAccordion(false);
        }
      }
    },
    [fieldValues, insert, remove, selectedTargets],
  );

  const handleAccordionChange = (index: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  return (
    <Box sx={{border: '1px dashed rgba(0,0,0,0.3)', borderRadius: 1.5, p: 2}}>
      <Box sx={{mb: 2}}>
        <Typography variant="h6" sx={{fontSize: 16}}>
          Configuración de campos para tareas por fila
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SwitchFormItem name={`${taskConfigPath}.enabled`} label="Habilitar envío de tareas" />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{mt: 2, p: 2}}>
        <Typography variant="subtitle2" sx={{mb: 1.5}}>
          Campos a incluir
        </Typography>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 0.5,
          }}>
          {IS_TASK_TARGET_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selectedTargets.has(opt.value)}
                  onChange={(e) => handleToggleTarget(opt.value, e.target.checked)}
                  size="small"
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>
      </Paper>

      {fields.map((field, index) => {
        const fieldData = fieldValues?.[index];
        const taskTarget = fieldData?.taskTarget || '';
        const responseType = fieldData?.responseType;
        const isEjecutor = taskTarget === 'ejecutor';
        const isValidadores = taskTarget === 'validadores';
        const showIsMultiple = MULTIPLE_BY_TARGET.has(taskTarget);
        const optionsName = `${fieldsPath}.${index}.options`;
        const optionsRaw = fieldData?.options;
        const optionsText = Array.isArray(optionsRaw)
          ? optionsRaw
              .map((opt) => opt?.label)
              .filter(Boolean)
              .join(', ')
          : '';
        const fieldLabel = IS_TASK_TARGET_OPTIONS.find((o) => o.value === taskTarget)?.label ?? taskTarget;

        return (
          <Accordion
            key={field.id}
            expanded={expandedAccordion === index}
            onChange={handleAccordionChange(index)}
            disableGutters
            sx={{
              mt: 1.5,
              boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
              '&:before': {display: 'none'},
            }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                '&.Mui-expanded': {minHeight: 48},
                '& .MuiAccordionSummary-content': {my: 1},
              }}>
              <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                {fieldLabel}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 0, pb: 2}}>
              <Grid container spacing={3} sx={{pl: 0.5, width: '100%'}}>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{opacity: 0.8, fontSize: 11}}>
                    Pregunta
                  </Typography>
                  <Box sx={{mt: 1}}>
                    <InputFormItem name={`${fieldsPath}.${index}.label`} label="Título" required />
                    {isEjecutor && (
                      <Box sx={{mt: 2}}>
                        <SelectFormItem
                          name={`${fieldsPath}.${index}.executorSource`}
                          label="Fuente del ejecutor"
                          options={EXECUTOR_SOURCE_OPTIONS}
                        />
                        {fieldData?.executorSource === 'location_from_question' && (
                          <Box sx={{mt: 2}}>
                            <SelectFormItem
                              name={`${fieldsPath}.${index}.executorLocationFieldKey`}
                              label="Pregunta de ubicación"
                              options={[
                                {value: '', label: 'Seleccione una pregunta de ubicación'},
                                ...locationQuestionOptions,
                              ]}
                            />
                          </Box>
                        )}
                        {fieldData?.executorSource === 'from_question' && (
                          <Box sx={{mt: 2}}>
                            <SelectFormItem
                              name={`${fieldsPath}.${index}.executorQuestionFieldKey`}
                              label="Desde pregunta"
                              options={[
                                {value: '', label: 'Seleccione una pregunta'},
                                ...userQuestionOptions,
                              ]}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    {isValidadores && (
                      <Box sx={{mt: 2}}>
                        <SelectFormItem
                          name={`${fieldsPath}.${index}.validatorSource`}
                          label="Fuente de los validadores"
                          options={VALIDATOR_SOURCE_OPTIONS}
                        />
                        {fieldData?.validatorSource === 'from_question' && (
                          <Box sx={{mt: 2}}>
                            <SelectFormItem
                              name={`${fieldsPath}.${index}.validatorQuestionFieldKey`}
                              label="Desde pregunta"
                              options={[
                                {value: '', label: 'Seleccione una pregunta'},
                                ...userQuestionOptions,
                              ]}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{opacity: 0.8, fontSize: 11}}>
                    Opciones
                  </Typography>
                  <Table size="small" sx={{mt: 1, '& td, & th': {border: 0, py: 0.5, verticalAlign: 'middle'}}}>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{width: 140, fontWeight: 500}}>
                          Obligatorio
                        </TableCell>
                        <TableCell>
                          <SwitchFormItem name={`${fieldsPath}.${index}.required`} label="" />
                        </TableCell>
                      </TableRow>
                      {showIsMultiple && (
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{fontWeight: 500}}>
                            Permitir múltiples
                          </TableCell>
                          <TableCell>
                            <SwitchFormItem name={`${fieldsPath}.${index}.isMultiple`} label="" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {false && (responseType === 'multipleChoice' || responseType === 'multiCheckbox') && (
                    <Box sx={{mt: 2}}>
                      <InputFormItem
                        name={`${fieldsPath}.${index}.__optionsText`}
                        label="Opciones (separadas por coma)"
                        defaultValue={optionsText}
                        onChange={(rawValue) => {
                          const text = rawValue ?? '';
                          const parsed = String(text)
                            .split(',')
                            .map((v) => v.trim())
                            .filter(Boolean)
                            .map((label, idx) => ({
                              id:
                                label
                                  .toLowerCase()
                                  .normalize('NFD')
                                  .replace(/[\u0300-\u036f]/g, '')
                                  .replace(/[^a-z0-9]+/g, '_')
                                  .replace(/^_+|_+$/g, '') || `opt_${idx + 1}`,
                              label,
                            }));
                          setValue(optionsName, parsed, {shouldDirty: true});
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {fields.length === 0 && (
        <Typography variant="body2" sx={{mt: 2, opacity: 0.7}}>
          Marque al menos un campo arriba para configurarlo.
        </Typography>
      )}
    </Box>
  );
}
